import classnames from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import { getShowingsMovieId } from '@cineamo/api/helpers/movie-helper';

import { IconBack24 } from '@cineamo/ui/src/icons/ic-back';
import { IconMenu24 } from '@cineamo/ui/src/icons/ic-menu';
import { IconShareBox24 } from '@cineamo/ui/src/icons/ic-share-box';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import ShowtimesFinderProvider, {
    useRequiredShowtimesFinder
} from '@cineamo/composables/src/providers/ShowtimesFinderProvider';
import { searchCitiesAction } from '@cineamo/composables/src/showtimes-finder/actions';
import DatesFilter from '@cineamo/composables/src/showtimes-finder/DateFilter';
import FilterSheet from '@cineamo/composables/src/showtimes-finder/FilterSheet';
import {
    PREVIEWS_SELECTED_DATE,
    resolveInitialSelectedDate
} from '@cineamo/composables/src/showtimes-finder/previewsUtils';
import SearchBar from '@cineamo/composables/src/showtimes-finder/SearchBar';
import ShowingsSearchResults from '@cineamo/composables/src/showtimes-finder/ShowingsSearchResults';
import {
    EMPTY_SHOWTIMES_FINDER_FILTERS,
    type ShowtimesFinderFilters
} from '@cineamo/composables/src/showtimes-finder/ShowtimesFinderFiltersPanel';
import {
    buildApiFilters,
    countActiveFilters
} from '@cineamo/composables/src/showtimes-finder/showtimesFinderFilterUtils';

const ShowtimesFinderMenuContent: React.FC<{
    isMenuOpen: boolean;
    onMenuClick: () => void;
    onClose: () => void;
    onShareClick: () => void;
    filters: ShowtimesFinderFilters;
    setFilters: (filters: ShowtimesFinderFilters) => void;
}> = ({ isMenuOpen, onMenuClick, onShareClick, onClose, filters, setFilters }) => {
    const { domainConfiguration, movie } = useDomainConfiguration();
    const { selectedDate, apiFilters } = useRequiredShowtimesFinder();
    const { track } = useAnalytics();
    const t = useTranslations();
    const [viewType, setViewType] = useState<'list' | 'map'>('list');
    const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);

    // Clear time filters when switching to a multi-day view — ALL or PREVIEWS —
    // since the time-of-day filter requires a specific date.
    useEffect(() => {
        const isMultiDay = selectedDate === 'all' || selectedDate === PREVIEWS_SELECTED_DATE;
        if (isMultiDay && filters.times.length > 0) {
            setFilters({ ...filters, times: [] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    const handleOpenFilter = () => {
        track('Showtimes Finder Menu Opened', {
            menu: 'filters',
            activeFilterCount: countActiveFilters(filters)
        });
        setIsFilterSheetOpen(true);
    };

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isMenuOpen) {
                onClose();
            }
        },
        [isMenuOpen, onClose]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);
    const movieTitle = movie.title;
    const heroImage = domainConfiguration?.heroBackdropUrl;
    const showtimesFinderHeader = domainConfiguration?.showtimesFinderHeader || 'artwork';
    const headerUsesColor = showtimesFinderHeader === 'primaryColor' || showtimesFinderHeader === 'secondaryColor';
    const headerColor =
        showtimesFinderHeader === 'primaryColor'
            ? domainConfiguration.style.primaryColor
            : showtimesFinderHeader === 'secondaryColor'
              ? domainConfiguration.style.secondaryColor || domainConfiguration.style.primaryColor
              : undefined;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={t('common.find-tickets')}
            aria-hidden={!isMenuOpen}
            style={
                {
                    '--widget-background-color': 'var(--background-color)',
                    '--search-background-color': 'var(--background-color)',
                    '--search-on-background-color': 'var(--on-background-color)',
                    '--date-button-font-size': '12px',
                    '--date-button-background-color': '#DBDBE2',
                    '--date-button-on-background-color': '#222222'
                } as React.CSSProperties
            }
            className={classnames(
                'absolute z-10 w-full h-full flex flex-row overflow-hidden',
                'transition-all duration-300 isolate',
                isMenuOpen ? 'right-0' : '-right-[100dvw]'
            )}>
            <div className="relative hidden w-1/4 xl:w-1/3 lg:block">
                <div className="absolute top-0 left-0 w-full h-full z-[-1] overflow-hidden">
                    {domainConfiguration.heroBackdropUrl && (
                        <div className="relative w-full h-full">
                            <div className="absolute top-0 left-0 w-full h-full backdrop-blur-[32px]" />
                            <Image
                                className="w-full h-full object-cover"
                                src={domainConfiguration.heroBackdropUrl}
                                alt={movieTitle ?? ''}
                                width={10000}
                                height={10000}
                            />
                        </div>
                    )}
                </div>
                <div className="flex flex-col h-full overflow-hidden">
                    <div className="px-16 py-9 w-full flex flex-row justify-between flex-shrink-0">
                        <button
                            className="p-12 cursor-pointer bg-[var(--primary-color)] rounded-4 text-[var(--on-primary-color)]"
                            onClick={() => {
                                onClose();
                                onMenuClick();
                            }}
                            aria-label={t('common.menu')}>
                            <IconMenu24 aria-hidden="true" />
                        </button>
                    </div>
                    {movie.posterImageURL_W780 && (
                        <div className="flex-1 flex items-center justify-center px-16 pb-16 overflow-hidden">
                            <div className="max-w-[384px] max-h-full aspect-[2/3] rounded-[12px] overflow-hidden">
                                <Image
                                    className="w-full h-full object-cover"
                                    src={movie.posterImageURL_W780}
                                    alt={movieTitle ?? ''}
                                    width={10000}
                                    height={10000}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="relative w-full lg:w-3/4 xl:w-2/3 flex flex-col h-full">
                {isFilterSheetOpen && (
                    <FilterSheet
                        selectedDate={selectedDate}
                        filters={filters}
                        setFilters={setFilters}
                        onClose={() => setIsFilterSheetOpen(false)}
                    />
                )}
                <div className="relative flex flex-col transition-all duration-300">
                    <div className="absolute left-0 top-0 w-full h-full flex flex-col z-[-1]">
                        {headerUsesColor ? (
                            <div
                                className="w-full h-full"
                                style={{ backgroundColor: headerColor }}
                            />
                        ) : (
                            heroImage && (
                                <Image
                                    className="w-full h-full object-cover"
                                    src={heroImage}
                                    width={10000}
                                    height={10000}
                                    alt={`${movieTitle} Backdrop`}
                                />
                            )
                        )}
                    </div>
                    <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-b from-black/20 to-black/0 z-[-1]" />
                    <div className="px-16 py-9 w-full flex flex-row justify-between mb-auto">
                        <button
                            className="p-12 cursor-pointer"
                            onClick={() => onClose()}
                            aria-label={t('common.back')}>
                            <IconBack24 aria-hidden="true" />
                        </button>
                        <button
                            className="p-12 cursor-pointer"
                            onClick={() => {
                                onClose();
                                onShareClick();
                            }}
                            aria-label={t('common.share')}>
                            <IconShareBox24 aria-hidden="true" />
                        </button>
                    </div>
                    {domainConfiguration.titleArtworkUrl && (
                        <div
                            className={classnames(
                                'grid transition-[grid-template-rows,opacity] duration-300 self-center',
                                // On mobile: collapse when map view
                                viewType === 'list' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                                // On desktop (lg): always show (grid-rows-[1fr] opacity-100)
                                'lg:grid-rows-[1fr] lg:opacity-100'
                            )}>
                            {!headerUsesColor && (
                                <div className="overflow-hidden flex items-center justify-center">
                                    <div className="max-w-[360px] max-h-[144px] mx-16 mb-16">
                                        <Image
                                            className="max-w-full max-h-[144px] w-auto h-auto object-contain"
                                            src={domainConfiguration.titleArtworkUrl}
                                            width={10000}
                                            height={10000}
                                            alt={`${movieTitle}`}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <SearchBar
                        viewType={viewType}
                        searchCitiesAction={searchCitiesAction}
                        isFilterable
                        onOpenFilter={handleOpenFilter}
                    />

                    <DatesFilter showPreviews={domainConfiguration?.showtimesFinderShowPremieres !== false} />
                </div>
                <ShowingsSearchResults
                    selectedDate={selectedDate}
                    viewType={viewType}
                    setViewType={setViewType}
                    movieId={getShowingsMovieId(movie)}
                    movieReleaseDate={movie.releaseDate}
                    apiFilters={apiFilters}
                    showShowingProperties
                />
            </div>
        </div>
    );
};

const ShowtimesFinderMenu: React.FC<{
    isMenuOpen: boolean;
    onMenuClick: () => void;
    onClose: () => void;
    onShareClick: () => void;
}> = (props) => {
    const { movie } = useDomainConfiguration();
    const [filters, setFilters] = useState<ShowtimesFinderFilters>(EMPTY_SHOWTIMES_FINDER_FILTERS);
    const apiFilters = useMemo(() => buildApiFilters(filters), [filters]);

    return (
        <ShowtimesFinderProvider
            movieId={getShowingsMovieId(movie)}
            // ALL while the movie is unreleased (its date buttons start at the release date,
            // so TODAY is not among them), today's date once it has released.
            initialSelectedDate={resolveInitialSelectedDate(undefined, movie.releaseDate)}
            apiFilters={apiFilters}
            releaseDate={movie.releaseDate}>
            <ShowtimesFinderMenuContent
                {...props}
                filters={filters}
                setFilters={setFilters}
            />
        </ShowtimesFinderProvider>
    );
};

export default ShowtimesFinderMenu;
